import { useState, useMemo, useEffect } from 'react';
import { MessageCircle, Heart, Zap, Bell, ArrowLeft, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationItem } from '@/components/notifications/NotificationItem';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Skeleton } from '@/components/ui/skeleton';
import { Navigation } from '@/components/Navigation';
import { useToast } from '@/hooks/useToast';

/**
 * NotificationsPage - Displays all user notifications (likes, comments, zaps)
 * for their music tracks and playlists.
 */

// Generate a random colorful gradient
function generateRandomGradient(): string[] {
  const gradients = [
    ['#667eea', '#764ba2'],
    ['#f093fb', '#f5576c'],
    ['#4facfe', '#00f2fe'],
    ['#43e97b', '#38f9d7'],
    ['#fa709a', '#fee140'],
    ['#30cfd0', '#330867'],
    ['#a8edea', '#fed6e3'],
    ['#ff9a56', '#ff6a88'],
    ['#ffecd2', '#fcb69f'],
    ['#ff6e7f', '#bfe9ff'],
    ['#e0c3fc', '#8ec5fc'],
    ['#f8b500', '#fceabb'],
    ['#fdcbf1', '#e6dee9'],
    ['#a1c4fd', '#c2e9fb'],
    ['#d299c2', '#fef9d7'],
    ['#89f7fe', '#66a6ff'],
    ['#fddb92', '#d1fdff'],
    ['#9890e3', '#b1f4cf'],
    ['#ebc0fd', '#d9ded8'],
    ['#96fbc4', '#f9f586'],
  ];

  return gradients[Math.floor(Math.random() * gradients.length)];
}

export function NotificationsPage() {
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const {
    notifications,
    commentNotifications,
    zapNotifications,
    likeNotifications,
    isLoading,
    refetch,
    markAllAsSeen,
  } = useNotifications(500); // Increased to fetch more historical notifications
  const [activeTab, setActiveTab] = useState<'all' | 'comments' | 'payments' | 'likes'>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Mark all notifications as seen when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      // Mark as seen when leaving the page
      if (notifications.length > 0) {
        markAllAsSeen();
      }
    };
  }, [notifications.length, markAllAsSeen]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
      toast({
        title: 'Notifications refreshed',
        description: 'Checked for new notifications',
      });
    } catch (error) {
      toast({
        title: 'Refresh failed',
        description: 'Could not refresh notifications. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Generate random gradient on component mount (page refresh)
  const gradient = useMemo(() => generateRandomGradient(), []);

  const backgroundStyle = {
    background: `linear-gradient(135deg, ${gradient.join(', ')})`,
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        {/* Blurred gradient background */}
        <div
          className="fixed top-0 left-0 right-0 h-[50vh] bg-cover bg-center blur-3xl opacity-45 pointer-events-none"
          style={backgroundStyle}
        />

        {/* Global Navigation */}
        <Navigation />

        {/* Content */}
        <div className="relative container mx-auto px-4 py-8 max-w-2xl">
          <div className="mb-6">
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </Link>
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Notifications</h1>
            <p className="text-muted-foreground">Activity on your tracks and playlists</p>
          </div>
          <Card className="p-12 glass border-white/10 text-center">
            <Bell className="h-12 w-12 mx-auto mb-4 text-white/40" />
            <h2 className="text-xl font-semibold mb-2">Log in to see notifications</h2>
            <p className="text-sm text-muted-foreground">
              Sign in to view likes, comments, and zaps on your content
            </p>
          </Card>
        </div>
      </div>
    );
  }

  const getTabContent = (tab: 'all' | 'comments' | 'payments' | 'likes') => {
    let items = notifications;

    if (tab === 'comments') items = commentNotifications;
    if (tab === 'payments') items = zapNotifications;
    if (tab === 'likes') items = likeNotifications;

    if (isLoading) {
      return (
        <div className="space-y-3 p-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex gap-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <div className="text-white/40 mb-2">
            {tab === 'all' && 'No notifications yet'}
            {tab === 'comments' && 'No comments yet'}
            {tab === 'payments' && 'No zaps yet'}
            {tab === 'likes' && 'No likes yet'}
          </div>
          <p className="text-sm text-muted-foreground max-w-md">
            {tab === 'all' && 'Activity on your tracks and playlists will show up here'}
            {tab === 'comments' && 'Comments on your tracks and playlists will appear here'}
            {tab === 'payments' && 'Lightning zaps on your content will appear here'}
            {tab === 'likes' && 'Likes on your tracks and playlists will appear here'}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-1 p-4">
        {items.map(notification => (
          <NotificationItem key={notification.event.id} notification={notification} />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Blurred gradient background */}
      <div
        className="fixed top-0 left-0 right-0 h-[50vh] bg-cover bg-center blur-3xl opacity-45 pointer-events-none"
        style={backgroundStyle}
      />

      {/* Global Navigation */}
      <Navigation />

      {/* Content */}
      <div className="relative container mx-auto px-4 py-8 max-w-2xl">
        <div className="mb-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </Link>
        </div>

        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Notifications</h1>
            <p className="text-muted-foreground">Activity on your tracks and playlists</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as typeof activeTab)}
          className="w-full"
        >
          <TabsList className="h-10 glass border-0 px-1 rounded-full w-full grid grid-cols-4 mb-4">
            <TabsTrigger
              value="all"
              className="text-xs px-2 rounded-full data-[state=active]:glass-strong"
            >
              <span>All</span>
              {notifications.length > 0 && (
                <span className="ml-2 text-xs bg-white/20 rounded-full px-2 py-0.5 min-w-[20px] text-center">
                  {notifications.length}
                </span>
              )}
            </TabsTrigger>

            <TabsTrigger
              value="comments"
              className="text-xs px-2 rounded-full data-[state=active]:glass-strong"
            >
              <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
              <span>Comments</span>
              {commentNotifications.length > 0 && (
                <span className="ml-2 text-xs bg-white/20 rounded-full px-2 py-0.5 min-w-[20px] text-center">
                  {commentNotifications.length}
                </span>
              )}
            </TabsTrigger>

            <TabsTrigger
              value="payments"
              className="text-xs px-2 rounded-full data-[state=active]:glass-strong"
            >
              <Zap className="h-3.5 w-3.5 mr-1.5" />
              <span>Zaps</span>
              {zapNotifications.length > 0 && (
                <span className="ml-2 text-xs bg-white/20 rounded-full px-2 py-0.5 min-w-[20px] text-center">
                  {zapNotifications.length}
                </span>
              )}
            </TabsTrigger>

            <TabsTrigger
              value="likes"
              className="text-xs px-2 rounded-full data-[state=active]:glass-strong"
            >
              <Heart className="h-3.5 w-3.5 mr-1.5" />
              <span>Likes</span>
              {likeNotifications.length > 0 && (
                <span className="ml-2 text-xs bg-white/20 rounded-full px-2 py-0.5 min-w-[20px] text-center">
                  {likeNotifications.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <Card className="glass border-white/10 overflow-hidden">
            <TabsContent value="all" className="mt-0">
              {getTabContent('all')}
            </TabsContent>
            <TabsContent value="comments" className="mt-0">
              {getTabContent('comments')}
            </TabsContent>
            <TabsContent value="payments" className="mt-0">
              {getTabContent('payments')}
            </TabsContent>
            <TabsContent value="likes" className="mt-0">
              {getTabContent('likes')}
            </TabsContent>
          </Card>
        </Tabs>
      </div>
    </div>
  );
}
